import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import i18next from 'i18next'
import { toast } from 'react-toastify'

import { MAX_CLUSTER_ZOOM } from '../constants/map'
import { getClusters, getLocations } from '../utils/api'
import { currentPathWithView } from '../utils/appUrl'
import isNetworkError from '../utils/isNetworkError'
import {
  addNewLocation,
  deleteExistingLocation,
  editExistingLocation,
} from './locationSlice'
import { addLocationToList, removeLocationFromList } from './saveSlice'
import { selectParams } from './selectParams'
import { updateSelection } from './updateSelection'

export const fetchMapLocations = createAsyncThunk(
  'map/fetchMapLocations',
  async (_, { getState }) => {
    const state = getState()
    const { types, muni } = state.filter
    const { lastMapView } = state.viewport

    if (lastMapView) {
      const { bounds, zoom } = lastMapView
      return await getLocations(
        selectParams(
          { types, muni, bounds, zoom, center: undefined },
          { limit: 1000 },
        ),
      )
    } else {
      return []
    }
  },
)

export const fetchMapClusters = createAsyncThunk(
  'map/fetchMapClusters',
  async (_, { getState }) => {
    const state = getState()
    const { types, muni } = state.filter
    const { lastMapView } = state.viewport

    if (lastMapView) {
      const { bounds, zoom } = lastMapView
      return await getClusters(
        selectParams({
          types,
          muni,
          bounds,
          zoom: Math.min(zoom + 1, MAX_CLUSTER_ZOOM),
          center: undefined,
        }),
      )
    } else {
      return []
    }
  },
)

const mapSlice = createSlice({
  name: 'map',
  initialState: {
    initialView: null,
    isLoading: true,
    locations: [],
    isFilterUpdated: false,
    clusters: [],
    googleMap: null,
    getGoogleMaps: null,
    placesReady: false,
    geometryReady: false,
  },
  reducers: {
    setGoogle: (state, action) => {
      state.googleMap = action.payload.googleMap
      state.getGoogleMaps = action.payload.getGoogleMaps
    },
    setPlacesReady: (state, action) => {
      state.placesReady = action.payload
    },
    setGeometryReady: (state, action) => {
      state.geometryReady = action.payload
    },
    disconnectMap: (state) => {
      state.initialView = null
      state.googleMap = null
      state.getGoogleMaps = null
      state.placesReady = false
      state.geometryReady = false
    },
    setInitialView: (state, action) => {
      state.initialView = action.payload

      const newUrl = currentPathWithView(state.initialView)

      const { pathname, search } = window.location
      const currentUrl = `${pathname}${search}`
      if (currentUrl !== newUrl) {
        window.history.pushState({}, '', newUrl)
      }
    },
  },
  extraReducers: {
    [updateSelection]: (state) => {
      state.isFilterUpdated = true
    },
    [fetchMapLocations.pending]: (state) => {
      state.isLoading = true
    },
    [fetchMapLocations.rejected]: (state, action) => {
      state.isLoading = false
      if (!isNetworkError(action.error)) {
        toast.error(
          i18next.t('error_message.api.fetch_locations_failed', {
            message:
              action.error.message || i18next.t('error_message.unknown_error'),
          }),
        )
      }
    },
    [fetchMapClusters.pending]: (state) => {
      state.isLoading = true
    },
    [fetchMapClusters.rejected]: (state, action) => {
      state.isLoading = false
      if (!isNetworkError(action.error)) {
        toast.error(
          i18next.t('error_message.api.fetch_clusters_failed', {
            message:
              action.error.message || i18next.t('error_message.unknown_error'),
          }),
        )
      }
    },
    [editExistingLocation.fulfilled]: (state, action) => {
      if (state.clusters.length) {
        return
      }
      const index = state.locations.findIndex(
        (loc) => loc.id === action.payload.id,
      )
      if (index !== -1) {
        state.locations[index] = {
          ...action.payload,
          in_list: state.locations[index].in_list,
        }
      }
    },
    [addNewLocation.fulfilled]: (state, action) => {
      if (state.clusters.length) {
        return
      }
      state.locations.push(action.payload)
    },
    [deleteExistingLocation.fulfilled]: (state, action) => {
      if (state.clusters.length) {
        return
      }
      state.locations = state.locations.filter(
        (loc) => loc.id !== action.payload,
      )
    },
    [fetchMapLocations.fulfilled]: (state, action) => {
      if (!state.googleMap) {
        return
      }
      const { north, east, south, west } = state.googleMap.getBounds().toJSON()

      if (state.isFilterUpdated) {
        state.locations = action.payload
        state.isFilterUpdated = false
      } else {
        const locationsInBounds = state.locations.filter(
          ({ lat, lng }) =>
            lat <= north && lng <= east && lat >= south && lng >= west,
        )
        const locationMap = new Map()

        locationsInBounds.forEach((loc) => {
          locationMap.set(loc.id, loc)
        })

        // add/overwrite with payload locations (these take priority)
        action.payload.forEach((loc) => {
          locationMap.set(loc.id, loc)
        })

        state.locations = Array.from(locationMap.values())
      }

      state.clusters = []
      state.isLoading = false
    },
    [fetchMapClusters.fulfilled]: (state, action) => {
      state.clusters = action.payload
      state.locations = []
      state.isLoading = false
    },
    [addLocationToList.fulfilled]: (state, action) => {
      const { location } = action.payload
      const index = state.locations.findIndex((loc) => loc.id === location.id)
      if (index !== -1) {
        state.locations[index].in_list = true
      }
    },
    [removeLocationFromList.fulfilled]: (state, action) => {
      const { locationId, locationStillInAnyList } = action.payload
      const index = state.locations.findIndex((loc) => loc.id === locationId)
      if (index !== -1) {
        state.locations[index].in_list = locationStillInAnyList
      }
    },
  },
})

export const {
  disconnectMap,
  setGoogle,
  setInitialView,
  setPlacesReady,
  setGeometryReady,
} = mapSlice.actions

export default mapSlice.reducer
